'use client';

import { useTranslations } from 'next-intl';
import {
  Eye,
  Pencil,
  Copy,
  Trash2,
  ClipboardList,
  ToggleLeft,
  ToggleRight,
  Star,
  EllipsisVertical
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { PermissionGate } from '@/features/auth';
import type { DynamicForm, DynamicFormType } from '../types';

interface DynamicFormCardProps {
  form: DynamicForm;
  onPreview: (form: DynamicForm) => void;
  onEdit: (form: DynamicForm) => void;
  onDuplicate: (form: DynamicForm) => void;
  onDelete: (form: DynamicForm) => void;
  onToggleStatus: (form: DynamicForm) => void;
  onToggleDefault: (form: DynamicForm) => void;
  isDuplicating?: boolean;
  isTogglingStatus?: boolean;
  isTogglingDefault?: boolean;
}

const typeTranslationKeys: Record<DynamicFormType, string> = {
  registration: 'typeRegistration',
  filling: 'typeFilling'
};

export function DynamicFormCard({
  form,
  onPreview,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleStatus,
  onToggleDefault
}: DynamicFormCardProps) {
  const t = useTranslations('dynamicForms');

  const formattedDate = new Date(form.updated_at).toLocaleDateString();

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between'>
          <div className='flex items-center gap-2'>
            <Badge
              variant='default'
              className={cn(
                form.status === 'active'
                  ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
                  : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
              )}
            >
              {form.status === 'active' ? t('statusActive') : t('statusDraft')}
            </Badge>
            {form.is_default && (
              <Badge
                variant='outline'
                className='gap-1 border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400'
              >
                <Star className='h-3 w-3 fill-current' />
                {t('setDefault')}
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                <EllipsisVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => onPreview(form)}>
                <Eye className='me-2 h-3.5 w-3.5' />
                {t('preview')}
              </DropdownMenuItem>
              <PermissionGate permissions={['dynamic_forms.edit']}>
                <DropdownMenuItem onClick={() => onEdit(form)}>
                  <Pencil className='me-2 h-3.5 w-3.5' />
                  {t('edit')}
                </DropdownMenuItem>
              </PermissionGate>
              <PermissionGate permissions={['dynamic_forms.create']}>
                <DropdownMenuItem onClick={() => onDuplicate(form)}>
                  <Copy className='me-2 h-3.5 w-3.5' />
                  {t('duplicate')}
                </DropdownMenuItem>
              </PermissionGate>
              <DropdownMenuSeparator />
              <PermissionGate permissions={['dynamic_forms.edit']}>
                <DropdownMenuItem onClick={() => onToggleStatus(form)}>
                  {form.status === 'active' ? (
                    <ToggleRight className='me-2 h-3.5 w-3.5' />
                  ) : (
                    <ToggleLeft className='me-2 h-3.5 w-3.5' />
                  )}
                  {form.status === 'active' ? t('deactivate') : t('activate')}
                </DropdownMenuItem>
              </PermissionGate>
              <PermissionGate permissions={['dynamic_forms.edit']}>
                <DropdownMenuItem onClick={() => onToggleDefault(form)}>
                  <Star
                    className={cn(
                      'me-2 h-3.5 w-3.5',
                      form.is_default && 'fill-current'
                    )}
                  />
                  {form.is_default ? t('unsetDefault') : t('setDefault')}
                </DropdownMenuItem>
              </PermissionGate>
              <DropdownMenuSeparator />
              <PermissionGate permissions={['dynamic_forms.delete']}>
                <DropdownMenuItem
                  className='text-destructive focus:text-destructive'
                  onClick={() => onDelete(form)}
                >
                  <Trash2 className='me-2 h-3.5 w-3.5' />
                  {t('delete')}
                </DropdownMenuItem>
              </PermissionGate>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <h3 className='mb-1 text-lg font-semibold'>{form.name}</h3>
        <p className='text-muted-foreground mb-2 text-sm'>
          {form.form_id_mask} &bull; v{form.version}
        </p>

        {form.description && (
          <p className='text-muted-foreground mb-4 line-clamp-2 text-sm'>
            {form.description}
          </p>
        )}

        <div className='mb-4'>
          <Badge variant='outline' className='gap-1'>
            <ClipboardList className='h-3 w-3' />
            {t(typeTranslationKeys[form.type])}
          </Badge>
        </div>

        <div className='mb-4 grid grid-cols-2 gap-4 text-center'>
          <div>
            <p className='text-muted-foreground text-sm'>{t('sections')}</p>
            <p className='text-primary text-2xl font-bold'>
              {form.fields_count ?? form.fields?.length ?? 0}
            </p>
          </div>
          <div>
            <p className='text-muted-foreground text-sm'>{t('fields')}</p>
            <p className='text-primary text-2xl font-bold'>
              {form.fields_count ?? form.fields?.length ?? 0}
            </p>
          </div>
        </div>

        <p className='text-muted-foreground text-xs'>
          {t('lastUpdated')}: {formattedDate}
        </p>
      </CardContent>
    </Card>
  );
}
